import { IFooterDTO } from "@/interfaces/IFooterDTO";

import gridCol from "@/utils/gridCol";
import CustomIcon from "../CustomIcon/CustomIcon";
import availableSites from "@/config/sitesConfig";
import { MutableRefObject, useRef, useState } from "react";
import { useOutsideClick } from "@/hooks";
import getPathByLink from "../../utils/getPathByLink";
import { useTranslation } from "react-i18next";
import Image from "next/image";

interface IFooterProps {
  dataFooter: IFooterDTO[];
  lang: string;
}

const Footer: React.FC<IFooterProps> = ({ dataFooter, lang }: IFooterProps) => {
  const { t, i18n } = useTranslation();
  const moduleData: IFooterDTO | undefined = dataFooter[0];

  const language = lang ? lang : i18n.language;

  const lenghtlstSiteMap: number = moduleData?.lstSiteMap?.length ? moduleData?.lstSiteMap.length : 0;
  const lenghtlstSocial: number = moduleData?.lstSocial?.length ? moduleData?.lstSocial.length : 0;
  const lenghtlstAppStore: number = moduleData?.lstAppStore?.length ? moduleData?.lstAppStore.length : 0;

  const flgTarget: string = moduleData?.flgTarget ? "_blank" : "_self";
  const flgTargetPrm: string = moduleData?.flgTargetPrm ? "_blank" : "_self";
  const flgTargetSea: string = moduleData?.flgTargetSea ? "_blank" : "_self";
  const upiLogoWhite: string = moduleData?.xtaxAirport?.ti?.upiLogoWhite
    ? moduleData.xtaxAirport.ti.upiLogoWhite.mediaserverpath + "/" + moduleData.xtaxAirport.ti.upiLogoWhite.resourcename
    : "";

  const upiImageDeskPrm: string = moduleData?.upiImageDeskPrm?.mediaserverpath
    ? moduleData?.upiImageDeskPrm?.mediaserverpath + "/" + moduleData?.upiImageDeskPrm?.resourcename
    : "";
  const upiImageDeskSea: string =
    moduleData?.upiImageDeskSea?.mediaserverpath + "/" + moduleData?.upiImageDeskSea?.resourcename;

  const txbSubtitle: string =
    moduleData?.txbSubtitle && moduleData?.txbSubtitle[language] ? moduleData?.txbSubtitle[language] : "";
  const lblCellphoneNumber: string =
    moduleData?.lblCellphoneNumber && moduleData?.lblCellphoneNumber[language]
      ? moduleData?.lblCellphoneNumber[language]
      : "";
  const lblAppStore: string =
    moduleData?.lblAppStore && moduleData?.lblAppStore[language] ? moduleData?.lblAppStore[language] : "";
  const lblLinkSea: string =
    moduleData?.lblLinkSea && moduleData?.lblLinkSea[language] ? moduleData?.lblLinkSea[language] : "";
  const lblLinkPrm: string =
    moduleData?.lblLinkPrm && moduleData?.lblLinkPrm[language]
      ? moduleData?.lblLinkPrm[language]
      : moduleData?.xconGenericPagePrm?.lblUrlPath && moduleData?.xconGenericPagePrm?.lblUrlPath[language]
      ? moduleData?.xconGenericPagePrm?.lblUrlPath[language]
      : "";

  const textCopyright: string = "Content Copyright " + new Date().getFullYear() + " S.E.A. S.p.A. P.IVA 00826040156";

  const siteList = availableSites();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  useOutsideClick(dropdownRef as MutableRefObject<HTMLDivElement>, () => {
    setIsDropdownOpen(false);
  });

  const LogoAirport = (
    <a href={`/${language}`} target={flgTarget} rel={flgTarget === "_blank" ? "noopener noreferrer" : undefined}>
      <div className="relative" style={{ width: 241, height: 29 }}>
        {upiLogoWhite ? (
          <Image src={upiLogoWhite} alt="logoAirport" fill sizes="241px" className="object-contain" unoptimized />
        ) : (
          <div aria-hidden="true" className="w-full h-full bg-gray-300 rounded" />
        )}
      </div>
    </a>
  );

  const text = <p className="desk:text-sm">{txbSubtitle}</p>;

  const seaIcon = (
    <a href={lblLinkSea} target={flgTargetSea} className="flex justify-end" rel={flgTargetSea === "_blank" ? "noopener noreferrer" : undefined}>
      <div className="relative" style={{ width: 120, height: 24 }}>
        {upiImageDeskSea ? (
          <Image src={upiImageDeskSea} alt="seaIcon" fill sizes="120px" className="object-contain" unoptimized />
        ) : (
          <div aria-hidden="true" className="w-full h-full bg-gray-300 rounded" />
        )}
      </div>
    </a>
  );

  const disabileIcon = upiImageDeskPrm ? (
    <a href={lblLinkPrm} target={flgTargetPrm} className="flex justify-end" rel={flgTargetPrm === "_blank" ? "noopener noreferrer" : undefined}>
      <div className="relative max-w-none" style={{ width: 40, height: 40 }}>
        <Image src={upiImageDeskPrm} alt="disableIcon" fill sizes="40px" className="object-contain" unoptimized />
      </div>
    </a>
  ) : null;

  const phone = lblCellphoneNumber ? (
    <a href={`tel:${lblCellphoneNumber}`} className="text-[#E6E6E6] flex gap-2 items-center font-bold desk:font-normal">
      <CustomIcon iconName="phone" className="fill-[#E6E6E6] w-6 h-6 desk:w-4 desk:h-4" />
      {lblCellphoneNumber}
    </a>
  ) : null;

  const appStore =
    lenghtlstAppStore == 0 ? null : (
      <div className="flex flex-col gap-2">
        <p className="desk:text-sm">{lblAppStore}</p>
        <div className="flex gap-4">
          {moduleData?.lstAppStore?.map((item) => {
            const itemId: number = item.id;
            const lblUrlAppStore: string = item.lblUrlAppStore;
            const upiImageDeskLogoAlt: string = item.upiImageDeskLogo.alt;
            const upiImageDeskLogo: string =
              item.upiImageDeskLogo.mediaserverpath + "/" + item.upiImageDeskLogo.resourcename;

            return (
              <a key={itemId} href={lblUrlAppStore} rel="noopener noreferrer" target="_blank">
                {/* w-28 ≈ 112px, riservo h=40 con object-contain */}
                <div className="relative" style={{ width: 112, height: 40 }}>
                  {upiImageDeskLogo ? (
                    <Image src={upiImageDeskLogo} alt={upiImageDeskLogoAlt} fill sizes="112px" className="object-contain" unoptimized />
                  ) : (
                    <div aria-hidden="true" className="w-full h-full bg-gray-300 rounded" />
                  )}
                </div>
              </a>
            );
          })}
        </div>
      </div>
    );

  const socialIcons =
    lenghtlstSocial == 0 ? null : (
      <div className="flex gap-11 desk:gap-5 tab:col-start-2">
        {moduleData?.lstSocial?.map((item) => {
          const itemId: number = item.id;
          const lblUrlSocial: string = item.lblUrlSocial;
          const flgTargetSocial: string = item.flgTargetSocial ? "_blank" : "_self"; // fix typo
          const upiImageSocial: string = item.upiImageSocial.mediaserverpath + "/" + item.upiImageSocial.resourcename;
          const upiImageSocialAlt: string = item.upiImageSocial.alt;

          return (
            <a key={itemId} href={lblUrlSocial} target={flgTargetSocial} aria-label={upiImageSocialAlt} rel={item.flgTargetSocial ? "noopener noreferrer" : undefined}>
              <div className="relative" style={{ width: 20, height: 20 }}>
                {upiImageSocial ? (
                  <Image src={upiImageSocial} alt={upiImageSocialAlt} fill sizes="20px" className="object-contain" unoptimized />
                ) : (
                  <div aria-hidden="true" className="w-full h-full bg-gray-300 rounded" />
                )}
              </div>
            </a>
          );
        })}
      </div>
    );

  const siteMapMobTab = (
    <div className="flex flex-col gap-5 py-3">
      {moduleData?.lstSiteMap?.map((item) => {
        const itemId: number = item.id;
        const lblTitle: string = item.lblTitle[language];
        const flgTargetSiteMap: string = item.flgTargetSiteMap ? "_blank" : "_self";
        const lblLinkSiteMap: string = item.lblLinkSiteMap[language];
        const [clicked, setClicked] = useState(false);

        return (
          <div key={itemId} className="flex flex-col" onClick={() => (!clicked ? setClicked(true) : setClicked(false))}>
            <div className="flex gap-3 items-center">
              {lblLinkSiteMap ? (
                <a href={lblLinkSiteMap} target={flgTargetSiteMap} className="text-[#E6E6E6] font-bold text-xl" rel={item.flgTargetSiteMap ? "noopener noreferrer" : undefined}>
                  {lblTitle}
                </a>
              ) : (
                <div className="text-[#E6E6E6] font-bold text-xl">{lblTitle}</div>
              )}
              <CustomIcon iconName="arrow" color={"#FFFFFF"} className="fill-[#FFFFFF] h-3 w-3" />
            </div>
            <div className={`${clicked ? "" : "hidden"} ml-3 mt-2 flex flex-col`}>
              {item.lstLink.map((el) => {
                const elId: number = el.id;
                const lblUrlField: string = el.lblUrlField[language];
                const flgTargetSiteMapInner: string = item.flgTargetSiteMap ? "_blank" : "_self";
                const lblFieldName: string = el.lblFieldName[language];

                return (
                  <a key={elId} href={lblUrlField} target={flgTargetSiteMapInner} className="pb-2 text-sm font-bold" rel={item.flgTargetSiteMap ? "noopener noreferrer" : undefined}>
                    {lblFieldName}
                  </a>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );

  const siteMapDesk = (
    <div className={`grid ${gridCol(lenghtlstSiteMap, 3)}`}>
      {moduleData?.lstSiteMap?.map((item) => {
        const itemId: number = item.id;
        const lblTitle: string = item.lblTitle[language];
        const flgTargetSiteMap: string = item.flgTargetSiteMap ? "_blank" : "_self";
        const lblLinkSiteMap: string = item.lblLinkSiteMap[language];

        return (
          <div key={itemId} className="flex flex-col px-4">
            {lblLinkSiteMap ? (
              <a href={lblLinkSiteMap} target={flgTargetSiteMap} className="text-[#E6E6E6] pb-8 text-sm" rel={item.flgTargetSiteMap ? "noopener noreferrer" : undefined}>
                {lblTitle}
              </a>
            ) : (
              <div className="text-[#E6E6E6] pb-8 text-sm" role="heading" aria-level={2}>
                {lblTitle}
              </div>
            )}

            {item.lstLink.map((el) => {
              const elId: number = el.id;
              const lblUrlField: string = el.lblUrlField[language];
              const flgTargetSiteMapInner: string = item.flgTargetSiteMap ? "_blank" : "_self";
              const lblFieldName: string = el.lblFieldName[language];

              return (
                <a key={elId} href={lblUrlField} target={flgTargetSiteMapInner} className="pb-2 text-sm font-bold" rel={item.flgTargetSiteMap ? "noopener noreferrer" : undefined}>
                  {lblFieldName}
                </a>
              );
            })}
          </div>
        );
      })}
    </div>
  );

  return (
    <>
      <div className="bg-[#333333] text-[#FFFFFF] flex flex-col gap-8">
        {/* MOBILE */}
        <div className="tab:hidden flex flex-col gap-8 px-6 pt-9">
          {LogoAirport}
          {text}
          {phone}
          {siteMapMobTab}
          {socialIcons}
        </div>

        {/* TABLET */}
        <div className="hidden tab:flex desk:hidden flex-col gap-8 px-6 pt-14">
          <div className="grid grid-cols-2 gap-28">
            <div className="flex flex-col gap-8">
              {LogoAirport}
              {text}
            </div>
            {siteMapMobTab}
          </div>
          <div className="grid grid-cols-2 items-center gap-28">
            {phone}
            {socialIcons}
          </div>
        </div>

        {/* DESKTOP */}
        <div className="hidden desk:flex px-6 pt-24 max-w-cont-1 mx-auto w-full">
          <div className="grid grid-cols-6 gap-16">
            <div className="flex flex-col gap-7 col-span-2 px-0">
              {LogoAirport}
              {text}
              {phone}
              {socialIcons}
            </div>
            <div className="col-span-4 flex justify-center">{siteMapDesk}</div>
          </div>

          <div>{disabileIcon}</div>
        </div>

        {/* Download Store */}
        <div className="grid grid-cols-5 pb-14 tab:pb-7 items-end max-w-cont-1 mx-auto px-6 w-full">
          <div className="col-span-4">{appStore}</div>
          <div className="block tab:hidden col-span-1">{disabileIcon}</div>
          <div className="hidden tab:block col-span-1">{seaIcon}</div>
        </div>
      </div>

      <div className="border-4 border-[#E20129]"></div>

      {/* SubFooter */}
      <div className="text-xs bg-lightblack ">
        <div className="p-6 flex justify-between flex-col tab:flex-row gap-y-5 max-w-cont-1 w-full mx-auto">
          {/* Airport switcher */}
          <div className="text-[#E6E6E6] font-bold flex gap-2">
            <div className="text-black">
              <div className="p-2 gap-3 flex text-sm text-lightgrey custom-select">
                <div>{t("homepage.footer_airport")}</div>
                <div className="relative">
                  <div
                    className="selectedOpt  gap-2 items-center flex cursor-pointer"
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  >
                    {process.env.NEXT_PUBLIC_AIRPORT_REFERENCE}{" "}
                    <CustomIcon color="lightgrey" className="rotate-90" iconName="chevron-r" />
                  </div>
                  {isDropdownOpen ? (
                    <div
                      ref={dropdownRef}
                      className="flex flex-col z-10 bg-lightblack border border-lightgrey p-2 rounded-xl absolute top-7 left-0"
                    >
                      {siteList.map((item) => {
                        return (
                          <a className="custom-select-opt cursor-pointer" key={item.id} href={item.siteUrl}>
                            {item.label}
                          </a>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>

          {/* Link Subfooter (Your Privacy Choices sotto "Privacy") */}
          <div className="flex flex-wrap gap-x-10 gap-y-3 tab:gap-x-5 desk:gap-x-10 items-start">
            {moduleData?.lstLinkSubfooter?.map((item) => {
              const itemId: number = item.id;
              const lblFieldNameSubfooter: string = item.lblFieldNameSubfooter[language];
              const flgTargetSiteMapSubFooter: string = item.flgTargetSiteMapSubFooter ? "_blank" : "_self";

              return (
                <div key={itemId} className="flex flex-col">
                  <a
                    href={getPathByLink(item, language)}
                    target={flgTargetSiteMapSubFooter}
                    className="text-[#FFFFFF] underline underline-offset-1 hover:no-underline"
                    rel={item.flgTargetSiteMapSubFooter ? "noopener noreferrer" : undefined}
                  >
                    {lblFieldNameSubfooter}
                  </a>

                  {/* Sotto a Privacy */}
                  {lblFieldNameSubfooter.toLowerCase() === "privacy" && (
                    <a
                      href="#"
                      className="iubenda-cs-preferences-link flex items-center mt-3 text-[#FFFFFF] underline underline-offset-1 hover:no-underline whitespace-nowrap"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 30 14"
                        className="w-4 h-4"
                        role="img"
                        aria-label="Your Privacy Choices"
                      >
                        <path
                          d="M22.6 0H7.4C3.5 0 .4 3.1.4 7s3.1 7 7 7h15.2c3.9 0 7-3.1 7-7s-3.1-7-7-7zM1.6 7c0-3.2 2.6-5.8 5.8-5.8h9.9l-3.1 11.6H7.4C4.2 12.8 1.6 10.2 1.6 7z"
                          fill="#0066FF"
                        />
                        <path
                          d="M24.6 4a.56.56 0 00-.8 0l-2.2 2.2-2.2-2.2a.56.56 0 10-.8.8L21 7l-2.2 2.2a.56.56 0 00.8.8l2.2-2.2 2.2 2.2a.56.56 0 00.8-.8L22.5 7l2.2-2.2a.56.56 0 000-.8z"
                          fill="#FFFFFF"
                        />
                        <path
                          d="M12.7 4.1a.56.56 0 00-.8 0L8 8.6 6.2 6.9a.56.56 0 10-.8.8l2.2 2.2a.56.56 0 00.8 0l4-4.7a.56.56 0 000-.8z"
                          fill="#0066FF"
                        />
                      </svg>
                      <span className="ml-1">
                        {language === "it" ? "Preferenze cookie" : "Cookie Preferences"}
                      </span>
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Copyright */}
        <p className="bg-[#E6E6E6] text-[#333333] p-3 text-center text-xs">{textCopyright}</p>
      </div>
    </>
  );
};

export default Footer;
